import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Menu, UserPlus } from "lucide-react";
import husselLogo from "@/assets/hussel-app-logo-wide.svg";
import { ChevronDown } from "lucide-react";

interface ContentPageNavProps {
  showPricing?: boolean;
}

export const ContentPageNav = ({ showPricing = false }: ContentPageNavProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [husselOpen, setHusselOpen] = useState(false);

  const menuItemsBeforeHusselen: { label: string; href: string; isAnchor?: boolean }[] = [
    { label: "Hoe werkt het?", href: "/hoe-werkt-het" },
    { label: "Over", href: "/over-de-hussel-app" },
  ];

  const menuItemsAfterHusselen: { label: string; href: string; isAnchor?: boolean }[] = [
    { label: "Reviews", href: "/reviews" },
    { label: "Prijzen", href: "/#pricing", isAnchor: true },
    { label: "Demo", href: "/demo" },
    { label: "Contact", href: "/contact" },
  ];

  const husselMenuItems = [
    { label: "Wat is husselen?", href: "/wat-is-husselen" },
    { label: "Wat is tossen?", href: "/wat-is-tossen" },
    { label: "Husselen vs tossen vs racket trekken", href: "/husselen-vs-tossen-vs-racket-trekken" },
    { label: "Husselavond organiseren", href: "/husselavond-organiseren" },
    { label: "Tossavond organiseren", href: "/tossavond-organiseren" },
    { label: "Racket trekken organiseren", href: "/racket-trekken-organiseren" },
    { label: "Tennisclub software", href: "/tennisclub-software" },
    { label: "Nieuws", href: "/nieuws" },
  ];

  const moreHusselItems = [
    { label: "App voor tennis hussel", href: "/tennis-hussel-app" },
    { label: "App voor tennis toss", href: "/tennis-toss-app" },
    { label: "AI systeem voor hussel & toss", href: "/ai-systeem-hussel-toss" },
    { label: "Hussel indelingen systeem", href: "/hussel-indelingen-systeem" },
    { label: "Toss indelingen systeem", href: "/toss-indelingen-systeem" },
    { label: "Spelers automatisch indelen", href: "/automatisch-indelen-tennisspelers" },
    { label: "Tennisvereniging app voor spelers", href: "/tennisvereniging-app-spelers" },
    { label: "Husselochtend organiseren", href: "/husselochtend-organiseren" },
    { label: "Tossochtend organiseren", href: "/tossochtend-organiseren" },
    { label: "Jeugd husselavond organiseren", href: "/jeugd-husselavond-organiseren" },
    { label: "Draaiboek husselavond", href: "/draaiboek-husselavond" },
    { label: "Meer spelers naar je husselavond", href: "/blog/meer-spelers-naar-husselavond" },
    { label: "Husselavond zonder vrijwilligers", href: "/blog/husselavond-zonder-vrijwilligers" },
  ];
  const [moreOpen, setMoreOpen] = useState(false);


  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-background shadow-sm">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <a
            href="/"
            aria-label="De Hussel App home"
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <img src={husselLogo} alt="De Hussel App" className="h-10 md:h-12 w-auto object-contain" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            {menuItemsBeforeHusselen.map((item) => (
              <a
                key={item.label}
                href={item.isAnchor ? "/#pricing" : item.href}
                className="text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
              >
                {item.label}
              </a>
            ))}
            
            {/* Husselen dropdown with hover */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent/50 data-[state=open]:bg-accent/50 data-[state=open]:text-foreground h-auto px-3 py-2 text-base font-normal">
                    Husselen
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[300px] gap-1 p-2 bg-popover max-h-[75vh] overflow-y-auto">
                      {husselMenuItems.map((item) => (
                        <li key={item.label}>
                          <NavigationMenuLink asChild>
                            <a
                              href={item.href}
                              className="block w-full text-left select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground cursor-pointer"
                            >
                              {item.label}
                            </a>
                          </NavigationMenuLink>
                        </li>
                      ))}
                      <li>
                        <button
                          onClick={() => setMoreOpen((o) => !o)}
                          aria-expanded={moreOpen}
                          className="flex w-full items-center justify-between rounded-md p-3 leading-none font-medium transition-colors hover:bg-muted cursor-pointer"
                        >
                          Meer hussel-content
                          <ChevronDown className={`h-4 w-4 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
                        </button>
                        {moreOpen && (
                          <ul className="ml-2 border-l border-border pl-2 grid gap-1">
                            {moreHusselItems.map((item) => (
                              <li key={item.href}>
                                <NavigationMenuLink asChild>
                                  <a
                                    href={item.href}
                                    className="block w-full text-left rounded-md p-2.5 text-sm leading-snug transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
                                  >
                                    {item.label}
                                  </a>
                                </NavigationMenuLink>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {menuItemsAfterHusselen.map((item) => (
              <a
                key={item.label}
                href={item.isAnchor ? "/#pricing" : item.href}
                className="text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
              >
                {item.label}
              </a>
            ))}
            <div className="flex items-center gap-3 ml-4">
              <Button asChild
                variant="outline"
                className="gap-1.5 hover:bg-accent hover:text-white hover:border-accent"
              >
                <a href="/auth">Login <span className="text-muted-foreground">/</span> <UserPlus className="h-4 w-4" /></a>
              </Button>
              <Button asChild
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <a href="/club-register">Club registreren</a>
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center gap-2">
            <Button asChild
              variant="outline"
              size="sm"
              className="gap-1.5 hover:bg-accent hover:text-white hover:border-accent"
            >
              <a href="/auth">Login <span className="text-muted-foreground">/</span> <UserPlus className="h-4 w-4" /></a>
            </Button>
            <DropdownMenu open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 max-h-[70vh] overflow-y-auto">
                {menuItemsBeforeHusselen.map((item) => (
                  <DropdownMenuItem key={item.label} asChild>
                    <a href={item.isAnchor ? "/#pricing" : item.href}>{item.label}</a>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setHusselOpen((o) => !o); }} className="justify-between">
                  Husselen <ChevronDown className={`h-4 w-4 transition-transform ${husselOpen ? "rotate-180" : ""}`} />
                </DropdownMenuItem>
                {husselOpen && (<div className="pl-3 border-l border-border ml-2">
                    {husselMenuItems.map((item) => (
                      <DropdownMenuItem key={item.label} asChild>
                        <a href={item.href}>{item.label}</a>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setMoreOpen((o) => !o); }} className="justify-between font-medium">
                      Meer hussel-content <ChevronDown className={`h-4 w-4 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
                    </DropdownMenuItem>
                    {moreOpen && (<div className="pl-3 border-l border-border ml-2">
                      {moreHusselItems.map((item) => (
                        <DropdownMenuItem key={item.href} asChild>
                          <a href={item.href}>{item.label}</a>
                        </DropdownMenuItem>
                      ))}
                    </div>)}
                  </div>)}
                {menuItemsAfterHusselen.map((item) => (
                  <DropdownMenuItem key={item.label} asChild>
                    <a href={item.isAnchor ? "/#pricing" : item.href}>{item.label}</a>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <a href="/club-register">Club registreren</a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};
